import { useMemo } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { type BouncerRoleUser } from "../../../services/bouncerUsers";
import { useUsersByRole } from "../hooks/useUsersByRole";

type Props = {
  showCm: boolean;
  /** Every Category Manager named. All are assigned; any one may review (ENG-27858). */
  cm: BouncerRoleUser[];
  onCmChange: (next: BouncerRoleUser[]) => void;
  /** Business Finance picker — off by default; most callers route Finance to its configured list. */
  showFm?: boolean;
  bizFin?: BouncerRoleUser | null;
  onFmChange?: (next: BouncerRoleUser | null) => void;
  cmHint?: string;
  fmHint?: string;
};

function optionLabel(u: BouncerRoleUser | null): string {
  return u ? `${u.user_name} · ${u.email_id}` : "";
}

export function ReviewerPickers({
  showCm,
  cm,
  onCmChange,
  showFm = false,
  bizFin = null,
  onFmChange = () => {},
  cmHint = "required — any one of them reviews after you",
  fmHint,
}: Props) {
  const cmQuery = useUsersByRole("CATEGORY_MANAGER", { enabled: showCm });
  const fmQuery = useUsersByRole("FINANCE_MANAGER", { enabled: showFm });
  const cmUsers = useMemo(
    () => (showCm ? cmQuery.data ?? [] : []),
    [showCm, cmQuery.data],
  );
  const fmUsers = useMemo(
    () => (showFm ? fmQuery.data ?? [] : []),
    [showFm, fmQuery.data],
  );
  const resolvedFmHint =
    fmHint ?? `required — reviews after ${showCm ? "Category Manager" : "you"}`;

  return (
    <>
      {showCm && (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>
            Category Manager reviewers{" "}
            <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
              ({cmHint})
            </Box>
          </Typography>
          <Autocomplete
            multiple
            filterSelectedOptions
            options={cmUsers}
            value={cm}
            onChange={(_, next) => onCmChange(next)}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(a, b) => a.user_id === b?.user_id}
            loading={cmQuery.isFetching}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder={cm.length ? "" : "Search by name or email"}
                inputProps={{
                  ...params.inputProps,
                  "aria-label": "Category Manager reviewer",
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {cmQuery.isFetching && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      )}

      {showFm && (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>
            Business Finance reviewer{" "}
            <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
              ({resolvedFmHint})
            </Box>
          </Typography>
          <Autocomplete
            options={fmUsers}
            value={bizFin}
            onChange={(_, next) => onFmChange(next)}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(a, b) => a.user_id === b?.user_id}
            loading={fmQuery.isFetching}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Search by name or email"
                inputProps={{
                  ...params.inputProps,
                  "aria-label": "Business Finance reviewer",
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {fmQuery.isFetching && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      )}
    </>
  );
}

export default ReviewerPickers;
